<template>
  <section
    class="modulo"
    :class="{ 'modulo--aberto': aberto }"
  >
    <button
      type="button"
      class="modulo__cabecalho"
      @click="aberto = !aberto"
    >
      <v-icon class="modulo__chevron">
        {{ aberto ? icons.mdiChevronUp : icons.mdiChevronDown }}
      </v-icon>

      <span class="modulo__nome">
        <span class="modulo__eyebrow">Módulo</span>
        {{ modulo.nomeExibicao }}
      </span>

      <span
        class="modulo__contador"
        :class="estadoContador"
      >
        {{ totalSelecionadasNoModulo }}/{{ totalFuncionalidadesNoModulo }}
      </span>
    </button>

    <div
      v-show="aberto"
      class="modulo__corpo"
    >
      <div
        v-for="agrupadora in modulo.funcionalidades"
        :key="agrupadora.id"
        class="agrupadora"
      >
        <div class="agrupadora__toolbar">
          <span class="agrupadora__nome">{{ agrupadora.nomeExibicao }}</span>

          <v-switch
            :input-value="todasSelecionadas(agrupadora)"
            :label="todasSelecionadas(agrupadora) ? 'Desmarcar tudo' : 'Marcar tudo'"
            dense
            hide-details
            class="agrupadora__marcar-tudo"
            @click="selecionarTodas(agrupadora)"
          />
        </div>

        <div class="agrupadora__checks">
          <v-checkbox
            v-for="funcionalidade in agrupadora.filhos"
            v-show="funcionalidadeBateBusca(funcionalidade)"
            :key="funcionalidade.id"
            :input-value="estaSelecionada(funcionalidade)"
            :label="funcionalidade.nomeExibicao"
            dense
            hide-details
            class="agrupadora__check"
            @click="alternarFuncionalidade(funcionalidade)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'

function normalizar(texto) {
  return (texto || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

export default {
  props: {
    modulo: {
      type: Object,
      required: true,
    },
    novaLista: {
      type: Object,
      required: true,
    },
    termoBusca: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const aberto = ref(false)

    const todasAsFuncionalidadesDoModulo = computed(() => props.modulo.funcionalidades.flatMap(a => a.filhos))

    const estaSelecionada = funcionalidade => funcionalidade.isChecked
      || props.novaLista.funcionalidadesIds.includes(funcionalidade.id)

    const totalFuncionalidadesNoModulo = computed(() => todasAsFuncionalidadesDoModulo.value.length)

    const totalSelecionadasNoModulo = computed(() => todasAsFuncionalidadesDoModulo.value
      .filter(f => estaSelecionada(f)).length)

    const estadoContador = computed(() => {
      if (totalSelecionadasNoModulo.value === 0) return 'modulo__contador--vazio'
      if (totalSelecionadasNoModulo.value === totalFuncionalidadesNoModulo.value) return 'modulo__contador--completo'

      return 'modulo__contador--parcial'
    })

    watch(() => props.termoBusca, termo => {
      if (normalizar(termo).trim()) aberto.value = true
    })

    function funcionalidadeBateBusca(funcionalidade) {
      const termo = normalizar(props.termoBusca).trim()

      return !termo || normalizar(funcionalidade.nomeExibicao).includes(termo)
    }

    function todasSelecionadas(agrupadora) {
      return agrupadora.filhos.every(f => estaSelecionada(f))
    }

    function alternarFuncionalidade(funcionalidade) {
      const jaSelecionada = estaSelecionada(funcionalidade)

      // eslint-disable-next-line no-param-reassign
      funcionalidade.isChecked = !jaSelecionada

      const indice = props.novaLista.funcionalidadesIds.indexOf(funcionalidade.id)

      if (jaSelecionada && indice !== -1) {
        props.novaLista.funcionalidadesIds.splice(indice, 1)
      } else if (!jaSelecionada && indice === -1) {
        props.novaLista.funcionalidadesIds.push(funcionalidade.id)
      }
    }

    function selecionarTodas(agrupadora) {
      const marcarTudo = !todasSelecionadas(agrupadora)

      agrupadora.filhos.forEach(funcionalidade => {
        if (estaSelecionada(funcionalidade) !== marcarTudo) {
          alternarFuncionalidade(funcionalidade)
        }
      })
    }

    return {
      aberto,
      totalFuncionalidadesNoModulo,
      totalSelecionadasNoModulo,
      estadoContador,
      estaSelecionada,
      funcionalidadeBateBusca,
      todasSelecionadas,
      alternarFuncionalidade,
      selecionarTodas,
      icons: {
        mdiChevronDown,
        mdiChevronUp,
      },
    }
  },
}
</script>

<style scoped>
.modulo {
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 8px;
  overflow: hidden;
}

.modulo__cabecalho {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: rgba(128, 128, 128, 0.08);
  border: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}

.modulo__nome {
  flex: 1;
  font-weight: 600;
  font-size: 14.5px;
}

.modulo__eyebrow {
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.55;
  margin-bottom: 2px;
}

.modulo__contador {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
  background: rgba(128, 128, 128, 0.18);
  opacity: 0.75;
}

.modulo__contador--parcial,
.modulo__contador--completo {
  background: rgba(109, 90, 237, 0.16);
  color: #6d5aed;
  opacity: 1;
}

.modulo__contador--completo {
  background: #6d5aed;
  color: #fff;
}

.modulo__corpo {
  padding: 4px 16px 12px;
}

.agrupadora {
  padding: 10px 0;
}

.agrupadora + .agrupadora {
  border-top: 1px solid rgba(128, 128, 128, 0.15);
}

.agrupadora__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.agrupadora__nome {
  font-size: 13px;
  opacity: 0.7;
  font-weight: 500;
}

.agrupadora__marcar-tudo {
  margin: 0;
  flex: none;
}

.agrupadora__checks {
  display: flex;
  flex-wrap: wrap;
  gap: 0 8px;
}

.agrupadora__check {
  margin: 0;
  flex: none;
}
</style>
